import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { fetchLiveBidsListings as fetchLiveBidsListingsApi } from '../../util/api';

const SLICE_NAME = 'liveBidsCarousel';

// ================ Async Thunks ================ //

export const fetchLiveBidsListingsThunk = createAsyncThunk(
  `${SLICE_NAME}/fetchLiveBidsListings`,
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchLiveBidsListingsApi();
      return data.data || [];
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ================ Slice ================ //

const initialState = {
  listings: [],
  inProgress: true, // start as true so spinner shows on first render before fetch completes
  error: null,
  lastFetchTime: null, // Track when data was last fetched
};

const liveBidsCarouselSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  extraReducers: builder => {
    builder
      .addCase(fetchLiveBidsListingsThunk.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchLiveBidsListingsThunk.fulfilled, (state, action) => {
        state.inProgress = false;
        state.listings = action.payload;
        state.error = null;
        state.lastFetchTime = Date.now(); // Store fetch time
      })
      .addCase(fetchLiveBidsListingsThunk.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      });
  },
});

export const liveBidsCarouselReducer = liveBidsCarouselSlice.reducer;

// ================ Selectors ================ //

export const liveBidsListingsSelector = state => state.liveBidsCarousel?.listings || [];
export const liveBidsInProgressSelector = state => state.liveBidsCarousel?.inProgress || false;
export const liveBidsErrorSelector = state => state.liveBidsCarousel?.error || null;
export const liveBidsLastFetchTimeSelector = state => state.liveBidsCarousel?.lastFetchTime || null;

// Check if data needs to be refreshed (older than 5 minutes)
export const liveBidsNeedsRefreshSelector = state => {
  const lastFetchTime = state.liveBidsCarousel?.lastFetchTime;
  if (!lastFetchTime) return true; // No data yet, needs fetch
  const fiveMinutesInMs = 5 * 60 * 1000;
  return Date.now() - lastFetchTime > fiveMinutesInMs;
};

// ================ Action Creators ================ //

export const fetchLiveBidsListings = () => dispatch => {
  return dispatch(fetchLiveBidsListingsThunk()).unwrap().catch(() => {
    // Silently fail - carousel will just not show
    return [];
  });
};
